import { forwardRef } from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { cn } from "@/lib/utils";

const sizeMap = { sm: 24, md: 32, lg: 40, xl: 56 } as const;
type AvatarSize = keyof typeof sizeMap;

interface AvatarProps
  extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> {
  size?: AvatarSize;
  name?: string;
  src?: string | null;
  colorSeed?: number;
}

/**
 * Warm-gradient fallback avatar. The gradient shifts per user via colorSeed
 * so a group of avatars stays visually distinct without pulling stock imagery.
 */
export const Avatar = forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  AvatarProps
>(({ className, size = "md", name, src, colorSeed = 0, style, ...props }, ref) => {
  const px = sizeMap[size];
  const initial = name?.trim().charAt(0).toUpperCase() ?? "?";

  // Rotate through a warm palette so 5 avatars in a row don't all look the same.
  const gradients = [
    "linear-gradient(135deg, var(--primary-soft), var(--warning-soft))",
    "linear-gradient(135deg, var(--success-soft), var(--warning-soft))",
    "linear-gradient(135deg, var(--danger-soft), var(--primary-soft))",
    "linear-gradient(135deg, var(--warning-soft), var(--success-soft))",
  ];
  const inks = ["var(--primary-ink)", "var(--success)", "var(--danger)", "var(--warning)"];
  const idx = ((colorSeed % gradients.length) + gradients.length) % gradients.length;

  return (
    <AvatarPrimitive.Root
      ref={ref}
      className={cn(
        "relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-bg-elevated",
        className,
      )}
      style={{ width: px, height: px, ...style }}
      {...props}
    >
      {src ? (
        <AvatarPrimitive.Image
          src={src}
          alt={name ?? "Avatar"}
          className="h-full w-full object-cover"
        />
      ) : null}
      <AvatarPrimitive.Fallback
        className="flex h-full w-full items-center justify-center font-display font-semibold"
        style={{
          background: gradients[idx],
          color: inks[idx],
          fontSize: Math.round(px * 0.42),
          fontVariationSettings: `"opsz" ${Math.max(24, px)}, "wght" 600`,
        }}
        delayMs={src ? 400 : 0}
      >
        {initial}
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  );
});
Avatar.displayName = "Avatar";

export function AvatarGroup({
  children,
  overlap = 10,
  className,
}: {
  children: React.ReactNode;
  overlap?: number;
  className?: string;
}) {
  return (
    <div
      className={cn("inline-flex items-center", className)}
      style={{ ["--overlap" as string]: `${overlap}px` }}
    >
      {Array.isArray(children)
        ? children.map((c, i) => (
            <div key={i} style={{ marginLeft: i === 0 ? 0 : `calc(-1 * var(--overlap))` }}>
              {c}
            </div>
          ))
        : children}
    </div>
  );
}
